import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { ENV } from "@/shared/lib/env";
import type { RootState } from "@/store";

/**
 * Action types dispatched as raw objects (not via imported action creators)
 * to avoid an import cycle:
 *
 *   adminApi.ts → baseQuery.ts → authSlice.ts
 *                              → adminApi.generated.ts (via Status enum)
 *                              → adminApi.ts  ← TDZ crash
 *
 * `createSlice({ name: "auth", reducers: { ... } })` emits exactly these
 * type strings; they must stay in sync with authSlice.
 */
const SESSION_EXPIRED = "auth/sessionExpired" as const;
const UPDATE_TOKENS = "auth/updateTokens" as const;

/**
 * BaseQuery custom:
 *  - Inyecta `apikey` (obligatorio por el gateway de Supabase) y
 *    `Authorization: Bearer <access_token>` en cada request.
 *  - Ante un 401/403 usa un `Mutex` para dedupar refresh concurrentes.
 *    Llama a `/admin/auth/refresh` directo con fetch (no via el
 *    baseQuery para no recursar), y reintenta la request original.
 *  - Si el refresh falla → `clearSession` → el router redirige al login.
 *
 * Mismo patrón que `store/getBaseQuery.ts` del app móvil (Arya app).
 */

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.VITE_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    headers.set("apikey", ENV.VITE_SUPABASE_ANON_KEY);
    const token = state.auth.accessToken;
    if (token) headers.set("authorization", `Bearer ${token}`);
    if (!headers.has("content-type")) headers.set("content-type", "application/json");
    return headers;
  },
});

async function callRefresh(
  refreshToken: string,
): Promise<{ access_token: string; refresh_token: string; expires_at: string } | null> {
  try {
    const res = await fetch(`${ENV.VITE_API_BASE_URL}/admin/auth/refresh`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: ENV.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      access_token: string;
      refresh_token: string;
      expires_at: string;
    };
    return data;
  } catch {
    return null;
  }
}

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);

  const isAuthError = result.error && (result.error.status === 401 || result.error.status === 403);
  if (!isAuthError) return result;

  const state = api.getState() as RootState;
  const refreshToken = state.auth.refreshToken;

  // No hay refresh token → limpiar sesión y devolver el error.
  if (!refreshToken) {
    api.dispatch({ type: SESSION_EXPIRED });
    return result;
  }

  if (!mutex.isLocked()) {
    const release = await mutex.acquire();
    try {
      const refreshed = await callRefresh(refreshToken);
      if (refreshed) {
        api.dispatch({
          type: UPDATE_TOKENS,
          payload: {
            accessToken: refreshed.access_token,
            refreshToken: refreshed.refresh_token,
            expiresAt: refreshed.expires_at,
          },
        });
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        api.dispatch({ type: SESSION_EXPIRED });
      }
    } finally {
      release();
    }
  } else {
    // Alguien más está refrescando; esperar y reintentar la request.
    await mutex.waitForUnlock();
    result = await rawBaseQuery(args, api, extraOptions);
  }

  return result;
};
